import { useMutation } from '@tanstack/react-query';

import { Collections, ShortUrl } from '@air/api';
import { type FullWorkspace } from '@air/types';
import { type ApiResponse, callAirApi } from '@air/utils-api';

type CreateCollectionResponse = ApiResponse<typeof Collections.create>;

import { queryClient } from '~/swr-hooks/queryClient';
import { getShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';

export type UseCreateCollectionMutationParams = {
  workspaceId: FullWorkspace['id'];
  collection: Omit<Required<Parameters<typeof Collections.create>>[0], 'workspaceId'>;
  shortUrl: Omit<Required<Parameters<typeof ShortUrl.getShortIdForCollection>>[0], 'workspaceId' | 'id'>;
};

export type UseCreateCollectionMutationReturn = {
  collection: Awaited<ReturnType<typeof Collections.create>>;
  shortUrl: Awaited<ReturnType<typeof ShortUrl.getShortIdForCollection>>;
};

export const useCreateCollectionMutation = () => {
  return useMutation<UseCreateCollectionMutationReturn, unknown, UseCreateCollectionMutationParams>({
    mutationFn: async ({ workspaceId, collection, shortUrl }) => {
      const createdCollection = await callAirApi<CreateCollectionResponse>(() =>
        Collections.create({
          workspaceId,
          ...collection,
        }),
      );

      if (!createdCollection.id) {
        throw new Error('Collection was created but no ID was returned');
      }

      // Create short URL for the collection
      const createdShortUrl = await ShortUrl.getShortIdForCollection({
        workspaceId,
        id: createdCollection.id,
        ...shortUrl,
      });

      return {
        collection: createdCollection,
        shortUrl: createdShortUrl,
      };
    },
    onSettled: (_data, _error, { workspaceId }) => {
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: getShortUrlsListKey({ workspaceId }) });
      }
    },
  });
};
